<template>
  <v-container>
    <page-heading>{{ pageTitle }}</page-heading>

    <v-card class="mb-4 px-5">
      <v-card-text>
        <v-row class="align-center">
          <v-col cols="2" class="text-end">
            {{ trans('import.lastImport') }}
          </v-col>
          <v-col cols="2">
            <b>{{ importData == null ? trans('general.unknown') : ( importData.lastImportedAt == null ? trans('import.never') : formatDate(importData.lastImportedAt, 'LLL') ) }}</b>
          </v-col>
          <v-col cols="8" class="text-end">
            <span v-if="importData != null && importData.runningSince != null" class="mr-5">
            {{ trans('import.currentlyRunning', {since: formatDate(importData.runningSince, 'LLL')}) }}
            </span>
            <v-btn color="primary" @click="tryImport" :disabled="importRunning">{{ trans('import.start') }}</v-btn>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <v-card v-if="importRunning">

      <v-card-text style="min-height: 300px" class="d-flex flex-column align-center justify-center text-center">
        <v-progress-circular indeterminate :size="58" color="primary"></v-progress-circular>
        <div class="text--secondary mt-6">{{ trans('general.isLoading') }}</div>
      </v-card-text>

    </v-card>

    <v-card v-else-if="importResult != null">

      <v-card-title>
        {{ trans('import.result') }}
      </v-card-title>

      <v-card-text>
        <v-simple-table>
          <thead>

          </thead>
          <tbody>
            <tr>
              <th></th>
              <th>{{ trans('import.created') }}</th>
              <th>{{ trans('import.updated') }}</th>
              <th>{{ trans('import.ignored') }}</th>
              <th>{{ trans('import.total') }}</th>
            </tr>
            <tr>
              <th>{{ transPlural('object.object') }}</th>
              <td>{{ importResult.createdObjects }}</td>
              <td>{{ importResult.updatedObjects }}</td>
              <td>-</td>
              <td>{{ importResult.createdObjects + importResult.updatedObjects }}</td>
            </tr>
            <tr>
              <th>{{ transPlural('guest.guests') }}</th>
              <td>{{ importResult.createdGuests }}</td>
              <td>{{ importResult.updatedGuests }}</td>
              <td>-</td>
              <td>{{ importResult.createdGuests + importResult.updatedGuests }}</td>
            </tr>
            <tr>
              <th>{{ transPlural('booking.booking') }}</th>
              <td>{{ importResult.createdBookings }}</td>
              <td>
                <v-chip outlined :title="trans('import.updatedBookings')">
                  <v-icon left>mdi-check</v-icon>
                  {{ importResult.updatedBookings - importResult.updatedEssentialsOnlyBookings }}
                </v-chip>
                <v-chip outlined class="ml-3" :title="trans('import.updatedEssentialsOnlyBookings')">
                  <v-icon left>mdi-lock-check-outline</v-icon>
                  {{ importResult.updatedEssentialsOnlyBookings }}
                </v-chip>
              </td>
              <td>
                <v-chip outlined :title="trans('import.ignoredByNotBlocked')">
                  <v-icon left>mdi-calendar-remove</v-icon>
                  {{ importResult.ignoredByNotBlocked }}
                </v-chip>
                <v-chip outlined class="ml-3" :title="trans('import.ignoredByPaymentBookings')">
                  <v-icon left>mdi-credit-card-off-outline</v-icon>
                  {{ importResult.ignoredByPaymentBookings }}
                </v-chip>
                <v-chip outlined class="ml-3" :title="trans('import.ignoredByRentBookings')">
                  <v-icon left>mdi-numeric-0-box</v-icon>
                  {{ importResult.ignoredByRentBookings }}
                </v-chip>
              </td>
              <td>{{ importResult.createdBookings + importResult.updatedBookings + importResult.ignoredByPaymentBookings + importResult.ignoredByRentBookings }}</td>
            </tr>
          </tbody>
        </v-simple-table>
      </v-card-text>

    </v-card>

    <validation-errors-modal
      :title="trans('error.general')"
      :show-modal.sync="showValidationErrorsModal"
      :validation-errors.sync="validationErrors"
    >
    </validation-errors-modal>

    <confirm-modal
        :show-modal="showConfirmModal"
        :title="trans('import.start')"
        @confirm="doImport(true)"
        @close="showConfirmModal = false"
    >
      <span v-if="importData != null">{{ trans('import.currentlyRunning', {since: formatDate(importData.runningSince, 'LLL')}) }}</span>
    </confirm-modal>

  </v-container>
</template>

<script>
import {LocaleUtils} from '../utils/locale-utils';
import PageHeading from '../components/PageHeading';
import {DateUtils} from '../utils/date-utils';
import {RouterUtils} from '../utils/router-utils';
import {MainUtils} from '@/utils/main-utils';
import {ImportApi} from '@/api/import-api';
import ConfirmModal from '@/components/ConfirmModal';
import ValidationErrorsModal from '../components/ValidationErrorsModal';

export default {
  name: 'SurveysPage',
  mixins: [
    LocaleUtils,
    DateUtils,
    RouterUtils,
    MainUtils
  ],
  components: {
    ValidationErrorsModal,
    ConfirmModal,
    PageHeading,
  },
  data: () => ({
    /** @type ImportStayinStatus */
    importData: null,
    /** @type ImportStayinResult */
    importResult: null,
    importRunning: false,
    showConfirmModal: false,

    showValidationErrorsModal: false,
    validationErrors: null
  }),
  computed: {
    pageTitle () {
      return this.trans('import.importStayin');
    }
  },
  mounted() {
    this.loadImportData();
  },
  methods: {
    loadImportData() {
      ImportApi.getLastStayinImport()
        .then(value => {
          this.importData = value.body;
        })
        .catch(this.handleErrorResponse);
    },
    tryImport() {
      if(this.importData == null || this.importData.runningSince != null) {
        this.showConfirmModal = true;
      } else {
        this.doImport(false);
      }
    },
    doImport(force) {
      if(!this.importRunning) {
        this.importRunning = true;

        ImportApi.doStayinImport(force)
            .then(value => {
              this.importResult = value.body;
              this.loadImportData();
            })
            .catch((error) => {
              this.validationErrors = this.getValidationErrors(error, false);
              this.showValidationErrorsModal = true;
            })
            .finally(() => {
              this.importRunning = false;
            })
        ;
      }
    }
  }
}
</script>

<style>
</style>
